import { useCallback, useEffect, useState } from "react";
import { FileSpreadsheet, FileText, Landmark, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Panel } from "@/components/KpiCard";
import { SmartTable } from "@/components/SmartTable";
import { api, download, errMsg, money } from "@/lib/api";
import { MargeSaisie, SoldeCourbe } from "@/components/HypothequeMarge";

const MOIS = ["Janv.", "Févr.", "Mars", "Avr.", "Mai", "Juin", "Juil.", "Août", "Sept.", "Oct.", "Nov.", "Déc."];
const AUTO = "__auto__";
const Th = ({ children, left }) => <th className={`whitespace-nowrap px-3 py-2 text-[11px] font-mono uppercase tracking-wider text-slate-500 ${left ? "text-left" : "text-right"}`}>{children}</th>;
const Td = ({ children, className = "", testId }) => <td className={`px-3 py-2.5 text-right tabular ${className}`} data-testid={testId}>{children}</td>;
const slug = (s) => s.replace(/[^\w-]+/g, "-");

const Exports = ({ annee }) => {
  const [busy, setBusy] = useState("");
  const dl = async (format) => {
    setBusy(format);
    try { await download("/hypotheque/export", { annee, format }, `Intérêts hypothécaires ${annee}.${format}`); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return (
    <div className="flex gap-2">
      <Button size="sm" variant="outline" className="h-8 bg-white" disabled={!!busy} onClick={() => dl("pdf")} data-testid="hypotheque-export-pdf">
        {busy === "pdf" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <FileText className="h-3.5 w-3.5 mr-1" />}Rapport PDF
      </Button>
      <Button size="sm" variant="outline" className="h-8 bg-white" disabled={!!busy} onClick={() => dl("xlsx")} data-testid="hypotheque-export-excel">
        {busy === "xlsx" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5 mr-1 text-emerald-700" />}Excel
      </Button>
    </div>
  );
};

const PretsChoix = ({ prets, immeubles, onChange }) => (
  <div className="mb-5 flex flex-wrap gap-3" data-testid="hypotheque-prets">
    {prets.map((p) => (
      <div key={p.pret} className="flex w-full flex-wrap items-center gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm sm:w-auto" data-testid={`hypotheque-pret-${slug(p.pret)}`}>
        <Landmark className="h-4 w-4 text-emerald-700" />
        <span><span className="font-medium">{p.type === "marge" ? (p.pret.startsWith("Marge") ? "" : "Marge de crédit ") : "Prêt hypothécaire "}{p.pret}</span><span className="block text-xs text-slate-500">{p.solde_fin == null ? "Solde non saisi" : `Solde au dernier relevé : ${money(p.solde_fin)}`}</span></span>
        <Select value={p.auto ? AUTO : p.immeuble} onValueChange={(v) => onChange(p.pret, v === AUTO ? "" : v)}>
          <SelectTrigger className="h-8 w-full bg-white sm:w-64" data-testid={`hypotheque-pret-select-${slug(p.pret)}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={AUTO}>Automatique ({p.auto ? p.immeuble : "selon le paiement"})</SelectItem>
            {immeubles.map((n) => <SelectItem key={n} value={n} data-testid={`hypotheque-pret-option-${slug(n)}`}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    ))}
  </div>
);

const TotalTable = ({ h }) => (
  <SmartTable id="hypotheque-total" data-export-titre="Intérêts par immeuble" className="w-full min-w-[640px] text-sm" data-testid="hypotheque-total-table">
    <thead><tr><Th left>Immeuble</Th><Th>Intérêts (déductibles)</Th><Th>Capital remboursé</Th><Th>Paiements totaux</Th></tr></thead>
    <tbody>
      {h.par_immeuble.map((g) => (
        <tr key={g.immeuble} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`hypotheque-immeuble-${slug(g.immeuble)}`}>
          <td className="px-3 py-2.5 font-medium">{g.immeuble}</td>
          <Td className="font-semibold text-emerald-700">{money(g.interets)}</Td><Td>{money(g.capital)}</Td><Td>{money(g.paiement)}</Td>
        </tr>
      ))}
      <tr className="border-t border-slate-200 bg-slate-50 font-semibold" data-st-fixe="">
        <td className="px-3 py-2.5">TOTAL</td>
        <Td testId="hypotheque-total-interets">{money(h.totaux.interets)}</Td><Td testId="hypotheque-total-capital">{money(h.totaux.capital)}</Td><Td>{money(h.totaux.paiement)}</Td>
      </tr>
    </tbody>
  </SmartTable>
);

const MensuelTable = ({ h }) => (
  <SmartTable id="hypotheque-mensuel" data-export-titre="Intérêts par mois" className="w-full min-w-[1100px] text-sm" data-testid="hypotheque-mensuel-table">
    <thead><tr><Th left>Immeuble</Th>{MOIS.map((m) => <Th key={m}>{m}</Th>)}<Th>Année</Th></tr></thead>
    <tbody>
      {h.par_immeuble.map((g) => (
        <tr key={g.immeuble} className="border-t border-slate-100">
          <td className="px-3 py-2.5 font-medium">{g.immeuble}</td>
          {MOIS.map((m, i) => <Td key={m} className="text-slate-600">{money(g.mois[String(i + 1).padStart(2, "0")] || 0)}</Td>)}
          <Td className="font-semibold">{money(g.interets)}</Td>
        </tr>
      ))}
    </tbody>
  </SmartTable>
);

const DetailTable = ({ h }) => (
  <SmartTable id="hypotheque-detail" data-export-titre="Détail des remboursements" className="w-full min-w-[900px] text-sm" data-testid="hypotheque-detail-table">
    <thead><tr><Th left>Date</Th><Th left>Prêt</Th><Th left>Immeuble</Th><Th>Paiement</Th><Th>Intérêts</Th><Th>Capital</Th><Th>Solde du prêt</Th></tr></thead>
    <tbody>
      {h.lignes.map((l) => (
        <tr key={`${l.pret}-${l.date}-${l.paiement}`} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`hypotheque-ligne-${l.date}`}>
          <td className="px-3 py-2.5 tabular">{l.date}</td><td className="px-3 py-2.5 font-mono text-xs">{l.pret}</td><td className="px-3 py-2.5">{l.immeuble}</td>
          <Td>{money(l.paiement)}</Td><Td className="font-medium text-emerald-700">{money(l.interets)}</Td><Td>{money(l.capital)}</Td><Td className="text-slate-600">{l.solde_pret == null ? "—" : money(l.solde_pret)}</Td>
        </tr>
      ))}
    </tbody>
  </SmartTable>
);

export const HypothequePanel = ({ annee, onChange }) => {
  const [h, setH] = useState(null);
  const load = useCallback(() => {
    api.get("/hypotheque", { params: { annee } }).then((r) => setH(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [annee]);
  useEffect(() => { load(); }, [load]);
  const recharger = () => { load(); onChange?.(); };
  const changer = async (pret, immeuble) => {
    try { await api.put("/hypotheque/pret", { pret, immeuble }); recharger(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Intérêts hypothécaires" action={h?.lignes.length ? <Exports annee={annee} /> : null} testId="bilan-hypotheque-panel">
      {!h ? <Loader2 className="h-5 w-5 animate-spin text-emerald-700" /> : (
        <div className="space-y-6">
          {!h.lignes.length ? (
            <p className="text-sm text-slate-500" data-testid="hypotheque-vide">Aucun remboursement de prêt hypothécaire pour {annee}. Importez (ou réimportez) les relevés Desjardins en CSV : la ligne du prêt (« Remboursement automatique ») indique les intérêts et le capital de chaque paiement.</p>
          ) : (
            <>
              <p className="text-xs text-slate-500">Les intérêts sont une dépense déductible des revenus de location (T776 / TP-128) ; le capital remboursé ne l'est pas, il réduit seulement le solde du prêt. Chiffres lus sur la ligne du prêt de chaque relevé Desjardins ou saisis pour la marge de crédit.</p>
              <PretsChoix prets={h.prets} immeubles={h.immeubles} onChange={changer} />
              <SoldeCourbe annee={annee} courbe={h.courbe} />
              <div className="-mx-5 overflow-x-auto"><TotalTable h={h} /></div>
              <div className="-mx-5 overflow-x-auto"><MensuelTable h={h} /></div>
              <div className="-mx-5 overflow-x-auto"><DetailTable h={h} /></div>
            </>
          )}
          <MargeSaisie annee={annee} lignes={h.marge_manuelle} onDone={recharger} />
        </div>
      )}
    </Panel>
  );
};
